import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Calculator,
  TrendingUp,
  DollarSign,
  Percent,
  Clock,
} from "lucide-react";
import {
  calculateCompoundInterest,
  type CompoundingFrequency,
  periodsPerYear,
} from "../../logic/compoundInterest";

const FREQUENCY_OPTIONS: {
  value: CompoundingFrequency;
  labelKey: string;
  periodsPerYear: number;
}[] = [
  {
    value: "annually",
    labelKey: "compoundCalculator.frequencies.annually",
    periodsPerYear: 1,
  },
  {
    value: "semiannually",
    labelKey: "compoundCalculator.frequencies.semiannually",
    periodsPerYear: 2,
  },
  {
    value: "quarterly",
    labelKey: "compoundCalculator.frequencies.quarterly",
    periodsPerYear: 4,
  },
  {
    value: "monthly",
    labelKey: "compoundCalculator.frequencies.monthly",
    periodsPerYear: 12,
  },
];

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function CompoundCalculator() {
  const { t } = useTranslation();

  const [principal, setPrincipal] = useState<string>("10000");
  const [annualRate, setAnnualRate] = useState<string>("7");
  const [years, setYears] = useState<string>("10");
  const [frequency, setFrequency] = useState<CompoundingFrequency>("annually");

  const result = useMemo(() => {
    const p = parseFloat(principal) || 0;
    const r = parseFloat(annualRate) || 0;
    const y = parseInt(years, 10) || 0;

    if (p <= 0 || r < 0 || y <= 0) {
      return null;
    }

    return calculateCompoundInterest(p, r, y, frequency);
  }, [principal, annualRate, years, frequency]);

  const handlePrincipalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setPrincipal(value);
    }
  };

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setAnnualRate(value);
    }
  };

  const handleYearsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setYears(value);
    }
  };

  return (
    <div className="compound-calculator">
      <div className="calculator-inputs">
        <div className="input-group">
          <label htmlFor="principal">
            <DollarSign className="input-icon" size={16} />
            {t("compoundCalculator.principalLabel")}
          </label>
          <div className="input-wrapper">
            <span className="currency-prefix">$</span>
            <input
              id="principal"
              type="text"
              inputMode="decimal"
              value={principal}
              onChange={handlePrincipalChange}
              placeholder="10000"
            />
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="rate">
            <Percent className="input-icon" size={16} />
            {t("compoundCalculator.rateLabel")}
          </label>
          <div className="input-wrapper">
            <input
              id="rate"
              type="text"
              inputMode="decimal"
              value={annualRate}
              onChange={handleRateChange}
              placeholder="7"
            />
            <span className="percent-suffix">%</span>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="years">
            <Clock className="input-icon" size={16} />
            {t("compoundCalculator.yearsLabel")}
          </label>
          <input
            id="years"
            type="text"
            inputMode="numeric"
            value={years}
            onChange={handleYearsChange}
            placeholder="10"
          />
        </div>

        <div className="input-group">
          <label htmlFor="frequency">
            <Calculator className="input-icon" size={16} />
            {t("compoundCalculator.frequencyLabel")}
          </label>
          <select
            id="frequency"
            value={frequency}
            onChange={(e) =>
              setFrequency(e.target.value as CompoundingFrequency)
            }
          >
            {FREQUENCY_OPTIONS.map((option) => {
              const safePeriods = periodsPerYear(option.value);
              return (
                <option
                  key={option.value}
                  value={option.value}
                  data-periods-per-year={safePeriods}
                >
                  {t(option.labelKey)}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {result && (
        <div className="calculator-results">
          <div className="result-cards">
            <div className="result-card primary">
              <div className="result-label">
                {t("compoundCalculator.finalAmount")}
              </div>
              <div className="result-value">
                {formatCurrency(result.finalAmount)}
              </div>
            </div>
            <div className="result-card">
              <div className="result-label">
                {t("compoundCalculator.totalInterest")}
              </div>
              <div className="result-value">
                {formatCurrency(result.totalInterest)}
              </div>
            </div>
            <div className="result-card">
              <div className="result-label">
                {t("compoundCalculator.totalPrincipal")}
              </div>
              <div className="result-value">
                {formatCurrency(parseFloat(principal) || 0)}
              </div>
            </div>
          </div>

          <div className="breakdown-section">
            <h3>
              <TrendingUp size={18} />
              {t("compoundCalculator.breakdownTitle")}
            </h3>
            <div className="breakdown-table-wrapper">
              <table className="breakdown-table">
                <thead>
                  <tr>
                    <th>{t("compoundCalculator.table.year")}</th>
                    <th>{t("compoundCalculator.table.startBalance")}</th>
                    <th>{t("compoundCalculator.table.interest")}</th>
                    <th>{t("compoundCalculator.table.endBalance")}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.breakdown.map((row) => (
                    <tr key={row.year}>
                      <td>{row.year}</td>
                      <td>{formatCurrency(row.startBalance)}</td>
                      <td className="interest-cell">
                        +{formatCurrency(row.interestEarned)}
                      </td>
                      <td>{formatCurrency(row.endBalance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!result && (
        <div className="calculator-placeholder">
          <Calculator size={48} />
          <p>{t("compoundCalculator.placeholder")}</p>
        </div>
      )}
    </div>
  );
}
